const ZOOM_BUCKETS = [0.125, 0.25, 0.5, 1, 2, 4, 8] as const;
const HIGH_ZOOM_BUCKET_STEP = 0.25;
/** Beyond this raster scale, tile count/resolution is capped — draw vectors live instead. */
export const MAX_TILE_ZOOM_BUCKET = 64;

export { ZOOM_BUCKETS };

export function zoomBucketForScale(scale: number): number {
  const minBucket = ZOOM_BUCKETS[0]!;
  const maxDiscreteBucket = ZOOM_BUCKETS[ZOOM_BUCKETS.length - 1]!;
  const clampedScale = Math.max(minBucket, scale);

  if (clampedScale > maxDiscreteBucket) {
    // Above the discrete ladder, rasterize at (at least) the display scale so tiles
    // are not upscaled past their source resolution at extreme zoom levels.
    const continuous = Math.ceil(clampedScale / HIGH_ZOOM_BUCKET_STEP) * HIGH_ZOOM_BUCKET_STEP;
    return Math.min(continuous, MAX_TILE_ZOOM_BUCKET);
  }

  let nearest: number = minBucket;
  let nearestDistance = Math.abs(Math.log2(clampedScale) - Math.log2(nearest));
  for (const bucket of ZOOM_BUCKETS) {
    const distance = Math.abs(Math.log2(clampedScale) - Math.log2(bucket));
    if (distance < nearestDistance) {
      nearest = bucket;
      nearestDistance = distance;
    }
  }
  return nearest;
}

/**
 * Tile bucket to use once the camera has settled (no active pan/zoom gesture).
 *
 * The discrete {@link zoomBucketForScale} ladder is great during gestures — a
 * stable bucket lets tiles be reused (and merely scaled) across a range of zoom
 * levels without re-rasterizing every frame. But at rest that same scaling
 * means a tile rasterized at, say, bucket 1 gets stretched to a 1.5x display
 * scale, so we composite an upscaled cached raster instead of crisp geometry.
 * This is exactly the "not quite sharp between 100% and 400%" symptom.
 *
 * At rest we instead rasterize at the *exact* display scale, so each tile texel
 * maps 1:1 to a screen pixel (composite scale == 1) and the vector geometry is
 * pixel-accurate. Clamped to the tile cap; beyond it callers draw vectors live.
 */
export function settledZoomBucketForScale(scale: number): number {
  const clamped = Math.max(ZOOM_BUCKETS[0]!, scale);
  return Math.min(clamped, MAX_TILE_ZOOM_BUCKET);
}

export function nearestCachedZoomBucket(scale: number, cachedBuckets: Iterable<number>): number {
  const target = zoomBucketForScale(scale);
  let best: number | undefined;
  for (const bucket of cachedBuckets) {
    if (bucket === target) {
      return target;
    }
    if (best === undefined || Math.abs(Math.log2(bucket) - Math.log2(scale)) < Math.abs(Math.log2(best) - Math.log2(scale))) {
      best = bucket;
    }
  }
  return best ?? target;
}

export function tileCompositeScale(actualScale: number, tileBucket: number): number {
  if (tileBucket <= 0) {
    return 1;
  }
  return actualScale / tileBucket;
}

export function requiresVectorLiveRender(displayScale: number): boolean {
  return displayScale > MAX_TILE_ZOOM_BUCKET + 1e-6;
}

export function zoomBucketLevels(): readonly number[] {
  return ZOOM_BUCKETS;
}
